import { useState, useEffect, useCallback, useRef } from "react";
import { saveWmtResults } from "../service/api";
import "../App.css";
import PageWrapper from "./PageWrapper";

const WMTTest = ({ userId, onNext }) => {
    const [step, setStep] = useState(0);
    const [gridSize, setGridSize] = useState([3, 3]);
    const [grid, setGrid] = useState([]);
    const [selectedTiles, setSelectedTiles] = useState(new Set());
    const [correctTiles, setCorrectTiles] = useState(new Set());
    const [isCorrect, setIsCorrect] = useState(null);
    const [consecutiveIncorrect, setConsecutiveIncorrect] = useState(0);

    const [startTime] = useState(Date.now());
    const [trialCount, setTrialCount] = useState(0);
    const [correctCount, setCorrectCount] = useState(0);
    const saveInFlight = useRef(false);
    const [saveError, setSaveError] = useState("");

    const saveResults = useCallback(async (final = true) => {
        const endTime = Date.now();
        const duration = Math.floor((endTime - startTime) / 1000);

        const payload = {
            userId,
            wmtTrialsCompleted: trialCount,
            wmtCorrectResponses: correctCount,
            wmtTimestamp: new Date(startTime).toISOString(),
            wmtElapsedSeconds: duration,
        };

        await saveWmtResults(payload);
        if (final) console.log("✅ WMT results saved.");
    }, [userId, trialCount, correctCount, startTime]);

    const finishTest = useCallback(async (final = true) => {
        if (saveInFlight.current) return;
        saveInFlight.current = true;
        setSaveError("");
        try {
            await saveResults(final);
            onNext();
        } catch (err) {
            console.error("❌ Error saving WMT results:", err);
            setSaveError("We could not save your working-memory results. Press the spacebar to try again.");
        } finally {
            saveInFlight.current = false;
        }
    }, [onNext, saveResults]);

    const advanceToNextGrid = useCallback(async () => {
        if (!isCorrect) {
            setStep(1);
            setSelectedTiles(new Set());
            return;
        }

        const nextGrid = {
            "3-3": [3, 4],
            "3-4": [4, 4],
            "4-4": [4, 5],
            "4-5": [5, 5],
            "5-5": [5, 6],
            "5-6": [6, 6],
            "6-6": [6, 7],
            "6-7": [7, 7],
            "7-7": [7, 8],
            "7-8": [8, 8],
        }[`${gridSize[0]}-${gridSize[1]}`];

        if (nextGrid) {
            setGridSize(nextGrid);
            setStep(1);
            setSelectedTiles(new Set());
        } else {
            await finishTest(false);
        }
    }, [isCorrect, gridSize, finishTest]);

    useEffect(() => {
        const handleKeyDown = async (event) => {
            if (event.code === "Space") {
                event.preventDefault();

                if (step === 0) {
                    setStep(1);
                } else if (step === 2) {
                    const userCorrect =
                        [...correctTiles].every((tile) => selectedTiles.has(tile)) &&
                        selectedTiles.size === correctTiles.size;

                    setIsCorrect(userCorrect);
                    setTrialCount((prev) => prev + 1);
                    setCorrectCount((prev) => (userCorrect ? prev + 1 : prev));
                    setConsecutiveIncorrect((prev) => (userCorrect ? 0 : prev + 1));
                    setStep(3);
                } else if (step === 3) {
                    if (consecutiveIncorrect >= 2) {
                        setStep(4);
                    } else {
                        await advanceToNextGrid();
                    }
                } else if (step === 4) {
                    await finishTest();
                }
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [
        step,
        selectedTiles,
        correctTiles,
        consecutiveIncorrect,
        advanceToNextGrid,
        finishTest,
    ]);

    useEffect(() => {
        if (step === 1) {
            generateGrid(gridSize[0], gridSize[1]);
            setTimeout(() => {
                setStep(2);
                setGrid(Array(gridSize[0]).fill().map(() => Array(gridSize[1]).fill("")));
            }, 2000);
        }
    }, [step, gridSize]);

    const generateGrid = (rows, cols) => {
        const newGrid = Array(rows).fill().map(() => Array(cols).fill("white"));
        const totalTiles = rows * cols;
        let blueTileCount = Math.ceil(totalTiles / 2);
        if (totalTiles % 2 === 0 && blueTileCount === totalTiles / 2) {
            blueTileCount += 1;
        }

        const allTiles = [];
        for (let r = 0; r < rows; r++) {
            for (let c = 0; c < cols; c++) {
                allTiles.push(`${r}-${c}`);
            }
        }

        const blueTiles = new Set();
        while (blueTiles.size < blueTileCount) {
            const randIndex = Math.floor(Math.random() * allTiles.length);
            blueTiles.add(allTiles[randIndex]);
        }

        const remainingTiles = allTiles.filter((tile) => !blueTiles.has(tile));
        const orangeTiles = new Set();
        while (orangeTiles.size < 2 && remainingTiles.length > 0) {
            const randIndex = Math.floor(Math.random() * remainingTiles.length);
            orangeTiles.add(remainingTiles.splice(randIndex, 1)[0]);
        }

        blueTiles.forEach((tile) => {
            const [r, c] = tile.split("-").map(Number);
            newGrid[r][c] = "blue";
        });

        orangeTiles.forEach((tile) => {
            const [r, c] = tile.split("-").map(Number);
            newGrid[r][c] = "orange";
        });

        setCorrectTiles(blueTiles);
        setGrid(newGrid);
    };

    const toggleTile = (row, col) => {
        const tileId = `${row}-${col}`;
        setSelectedTiles((prev) =>
            prev.has(tileId)
                ? new Set([...prev].filter((id) => id !== tileId))
                : new Set(prev).add(tileId)
        );
    };

    return (
        <PageWrapper maxWidth="900px">
        <div className="wmt-container" data-testid="wmt-test" data-step={step}>
            {step === 0 && <p className="submit-text">Press the <b>spacebar</b> to begin the test.</p>}
            {step === 1 && (
                <div
                    className="grid-container"
                    data-testid="wmt-test-pattern-grid"
                    style={{ gridTemplateColumns: `repeat(${gridSize[1]}, 50px)` }}
                >
                    {grid.map((row, rowIndex) =>
                        row.map((color, colIndex) => (
                            <div
                                key={`${rowIndex}-${colIndex}`}
                                className="grid-tile"
                                data-testid={`wmt-test-pattern-tile-${rowIndex}-${colIndex}`}
                                data-tile-id={`${rowIndex}-${colIndex}`}
                                data-color={color || "white"}
                                style={{ backgroundColor: color || "white" }}
                            />
                        ))
                    )}
                </div>
            )}
            {step === 2 && (
                <>
                    <p className="instruction-text">Try to replicate the pattern shown on the previous screen. Click tiles to select them.</p>
                    <div
                        className="grid-container"
                        data-testid="wmt-test-input-grid"
                        style={{ gridTemplateColumns: `repeat(${gridSize[1]}, 50px)` }}
                    >
                        {grid.map((row, rowIndex) =>
                            row.map((_, colIndex) => {
                                const tileId = `${rowIndex}-${colIndex}`;
                                return (
                                    <div
                                        key={tileId}
                                        className={`grid-tile ${selectedTiles.has(tileId) ? "selected" : ""}`}
                                        data-testid={`wmt-test-input-tile-${tileId}`}
                                        data-tile-id={tileId}
                                        onClick={() => toggleTile(rowIndex, colIndex)}
                                    />
                                );
                            })
                        )}
                    </div>
                    <p className="submit-text">Press the <b>spacebar</b> to submit.</p>
                </>
            )}
            {step === 3 && (
                <>
                    <p className="instruction-text" style={{ color: isCorrect ? "green" : "red" }}>
                        {isCorrect ? "✅ Correct! Well done!" : "Unfortunately, you did not correctly mark all the squares."}
                    </p>
                    {!isCorrect && (
                        <div className="comparison-table">
                            <table>
                                <thead>
                                    <tr><th>Correct Pattern</th><th>Your Selection</th></tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>
                                            <div
                                                className="grid-container"
                                                data-testid="wmt-test-correct-grid"
                                                style={{ gridTemplateColumns: `repeat(${gridSize[1]}, 50px)` }}
                                            >
                                                {Array.from({ length: gridSize[0] }).map((_, rowIndex) =>
                                                    Array.from({ length: gridSize[1] }).map((_, colIndex) => {
                                                        const tileId = `${rowIndex}-${colIndex}`;
                                                        return <div key={tileId} className={`grid-tile ${correctTiles.has(tileId) ? "selected" : ""}`} />;
                                                    })
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <div
                                                className="grid-container"
                                                data-testid="wmt-test-selected-grid"
                                                style={{ gridTemplateColumns: `repeat(${gridSize[1]}, 50px)` }}
                                            >
                                                {Array.from({ length: gridSize[0] }).map((_, rowIndex) =>
                                                    Array.from({ length: gridSize[1] }).map((_, colIndex) => {
                                                        const tileId = `${rowIndex}-${colIndex}`;
                                                        return <div key={tileId} className={`grid-tile ${selectedTiles.has(tileId) ? "selected" : ""}`} />;
                                                    })
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    )}
                    <p className="submit-text">Press the <b>spacebar</b> to proceed.</p>
                </>
            )}
            {step === 4 && <p className="submit-text">This test is now complete. Press the <b>spacebar</b> to proceed.</p>}
            {saveError && <p className="alert alert-error" role="alert">{saveError}</p>}
        </div>
        </PageWrapper>
    );
};

export default WMTTest;
